import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { PaymentMethodCard } from '@components/molecules/PaymentMethodCard';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { InputErrors, ShoppingCartTotalData } from '@types';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useGetAllPaymentMethods } from '@services/useGetAllPaymentMethods';
import { useAddPaymentMethodToCart } from '@services/useAddPaymentMethodToCart';
import { useNotifications } from '@hooks/useNotifications';
import { Button, ButtonMode } from '@components/atoms/Button';
import { formatPtPhoneNumberInput, removeWhiteSpaces } from '@utils/format';

const CURRENT_STEP = 4;

export interface PaymentProps {
    total: ShoppingCartTotalData;
}

interface PaymentErrors {
    phoneNumber: InputErrors;
}

export const Payment: FC<PaymentProps> = ({ total }) => {
    const { navigate } = useCustomNavigate();
    const { login, allPaymentMethods, cart } = useData();
    const { request: requestAllPaymentMethods } = useGetAllPaymentMethods();
    const {
        request: requestAddPaymentMethodToCart,
        loading: loadingAddPaymentMethodToCart,
        errorMessage: errorMessageAddPaymentMethodToCart,
        validationErrors: validationErrorsAddPaymentMethodToCart,
        lastResponseTime: lastResponseTimeAddPaymentMethodToCart,
    } = useAddPaymentMethodToCart();
    const { showDefaultNotification } = useNotifications();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('payment-component.title'),
        multibancoDescription: t('payment-component.multibanco.description'),
        mbwayDescription: t('payment-component.mbway.description'),
        mbwayPhoneNumber: t('payment-component.mbway.phone-number.label'),
        continueButton: t('payment-component.continue.button'),
    };

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    useEffect(() => {
        requestAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
    const [mbwayPhoneNumber, setMbwayPhoneNumber] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<PaymentErrors>({
        phoneNumber: [],
    });

    const multibancoMethod = allPaymentMethods?.data?.find((item) => item.method === 'multibanco');
    const mbwayMethod = allPaymentMethods?.data?.find((item) => item.method === 'mbway');
    const cardMethod = allPaymentMethods?.data?.find((item) => item.method === 'card');

    // check if user already previously selected a payment method
    useEffect(() => {
        const method = cart?.data?.paymentMethod?.method;
        const phoneNumber = cart?.data?.paymentMethod?.paymentPhoneNumber;

        if (method === 'mbway' && mbwayMethod) {
            setSelectedMethod(mbwayMethod.uuid || '');
            phoneNumber && setMbwayPhoneNumber(formatPtPhoneNumberInput(phoneNumber));
        } else if (method === 'multibanco' && multibancoMethod) {
            setSelectedMethod(multibancoMethod.uuid || '');
        } else if (method === 'card' && cardMethod) {
            setSelectedMethod(cardMethod.uuid || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.paymentMethod]);

    const handleSavePaymentMethod = () => {
        // prevent multiple instances of service being called
        !loadingAddPaymentMethodToCart &&
            requestAddPaymentMethodToCart({
                payment_method_uuid: selectedMethod || '',
                payment_phone_number: selectedMethod === mbwayMethod?.uuid ? removeWhiteSpaces(mbwayPhoneNumber) : undefined,
            });
    };

    useEffect(() => {
        if (lastResponseTimeAddPaymentMethodToCart) {
            if (errorMessageAddPaymentMethodToCart) {
                showDefaultNotification({
                    type: 'error',
                    message: errorMessageAddPaymentMethodToCart,
                });
            } else if (validationErrorsAddPaymentMethodToCart) {
                setErrors({
                    // match form request errors with frontend errors object
                    phoneNumber: validationErrorsAddPaymentMethodToCart?.payment_phone_number || [],
                });
            } else {
                navigate(`/${SITE_URLS.orderConfirm}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTimeAddPaymentMethodToCart]);

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        label={translations.continueButton}
                        mode={ButtonMode.primary}
                        onClick={handleSavePaymentMethod}
                        disabled={
                            // service is loading
                            loadingAddPaymentMethodToCart ||
                            // no method was selected
                            selectedMethod === null ||
                            // mbway is selected but no number given
                            (selectedMethod === mbwayMethod?.uuid && mbwayPhoneNumber.trim() === '')
                        }
                    />
                </div>
            }
            total={total}>
            <>
                {/* multibanco */}
                {multibancoMethod && (
                    <PaymentMethodCard
                        title={multibancoMethod.name || ''}
                        description={translations.multibancoDescription}
                        logo={multibancoMethod.image?.src}
                        radioButton={{
                            value: multibancoMethod.uuid || '',
                            checked: selectedMethod === multibancoMethod.uuid,
                            onChange: () => setSelectedMethod(multibancoMethod.uuid || ''),
                        }}
                    />
                )}

                {/* mbway */}
                {mbwayMethod && (
                    <PaymentMethodCard
                        title={mbwayMethod.name || ''}
                        description={translations.mbwayDescription}
                        logo={mbwayMethod.image?.src}
                        radioButton={{
                            value: mbwayMethod.uuid || '',
                            checked: selectedMethod === mbwayMethod.uuid,
                            onChange: () => setSelectedMethod(mbwayMethod.uuid || ''),
                        }}>
                        <div className="mt-4">
                            <Input
                                label={translations.mbwayPhoneNumber}
                                value={mbwayPhoneNumber}
                                onChange={(value) => setMbwayPhoneNumber(formatPtPhoneNumberInput(value))}
                                type={InputTypes.tel}
                                variant={InputVariants.labeledInput}
                                state={errors.phoneNumber.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.phoneNumber}
                            />
                        </div>
                    </PaymentMethodCard>
                )}

                {/* debit or credit card */}
                {cardMethod && (
                    <PaymentMethodCard
                        title={cardMethod.name || ''}
                        logo={cardMethod.image?.src}
                        radioButton={{
                            value: cardMethod.uuid || '',
                            checked: selectedMethod === cardMethod.uuid,
                            onChange: () => setSelectedMethod(cardMethod.uuid || ''),
                        }}
                    />
                )}
            </>
        </ShoppingCartContainer>
    );
};
